import type Phaser from "phaser";
import { makeRng } from "../util/seededRng.js";
import { DEPTH } from "./depthLayers.js";
import { BRICK_SHATTER_VARIANTS } from "./brickShatterMeta.generated.js";
import type { FxImagePool } from "./fxImagePool.js";

/**
 * 벽돌 파괴 조각(shatter) — 균열 variant 패턴 그대로 조각나는 파괴 연출.
 *
 * 중앙 분출(brickDebris)과 달리 조각이 벽돌 영역 내 "원래 위치"에서 출발한다:
 * 메타(brickShatterMeta.generated — 생성기 emit)의 정규화 bbox로 조각을 벽돌 크기에 비례 배치하고,
 * 벽돌 중심→조각 중심 방향(+지터)으로 흩어지며 회전·중력·페이드한다. 균열 오버레이와 조각 PNG가
 * 같은 분할 기하에서 파생되므로 "금 간 그래픽이 그 금대로 쪼개지는" 일치가 보장된다.
 *
 * 흩어짐 스타일(SHATTER_STYLES) — 파괴마다 랜덤 1종으로 연출 다양화 (회전은 전부 아주 약한 텀블):
 *  burst=사방 분출 / spin=빠른 ripple 흩뿌림 / fountain=위로 솟았다 낙하(Back.easeIn 아크) / collapse=무너져 내림.
 * 계획 산출(computeShatterPlan)·variant 배정(pickShatterVariant)은 순수 — rng 주입 결정론.
 */

/** 파괴 조각 variant 수 — 메타(생성기 emit)가 SSoT. */
export const SHATTER_VARIANT_COUNT = BRICK_SHATTER_VARIANTS.length;

// 비행 기본 파라미터 — 스타일 spec의 배수(distMul 등)가 여기에 곱해진다.
export const SHATTER_FLY_MIN = 24;
export const SHATTER_FLY_VAR = 26;
export const SHATTER_ANGLE_JITTER = (15 * Math.PI) / 180;
// 비행/회전 트윈 시간 — 거리·회전량은 그대로 두고 이 값을 키우면 퍼짐·회전 속도가 느려진다(천천히 흩어짐).
const BASE_DURATION = 640;
// 아주 약한 텀블(±~11°) — 조각이 팽글팽글 도는 대신 사방 확산이 주연출. 회전은 경직 방지용 미세 기울임만.
const BASE_ROTATION = Math.PI * 0.06;

export interface ShatterStyleSpec {
  /** 바깥 확산 거리 배수 (기본 24~50px에 곱). */
  readonly distMul: number;
  /** 하강 종착 오프셋(px, 양수=아래). */
  readonly fall: number;
  /** 회전 델타 배수 (기본 ±0.06π≈±11°에 곱 — 전부 약한 텀블, 스타일별 미세 차이만). */
  readonly rotMul: number;
  readonly durationMul: number;
  /** 조각별 시작 지연 상한(ms) — 0이면 동시, 클수록 와르르 무너지는 ripple. */
  readonly maxDelay: number;
  /** 종료 스케일 배수 (시작 스케일에 곱). */
  readonly endScale: number;
  /** y 이동 ease — Back.easeIn은 위로 솟았다 떨어지는 아크, Quad.easeIn은 자유낙하 가속. */
  readonly yEase: string;
}

/**
 * 흩어짐 스타일 spec — 모든 스타일은 다음 둘 중 하나의 불변식을 데이터로 보장해야 한다
 * (brickShatter.test.ts가 spec 수치에서 자동 판정 — 어느 쪽도 보장 못하는 스타일은 추가 불가):
 *  ① 바깥 분출: fall ≤ distMul × FLY_MIN × cos(JITTER) → 모든 조각이 중심 반대쪽으로 이동
 *  ② 하강 종착: fall > distMul × (FLY_MIN + FLY_VAR) → 모든 조각의 종착이 시작보다 아래
 */
export const SHATTER_STYLE_NAMES = ["burst", "spin", "fountain", "collapse"] as const;
export type ShatterStyle = (typeof SHATTER_STYLE_NAMES)[number];

export const SHATTER_STYLES: Record<ShatterStyle, ShatterStyleSpec> = {
  // 사방 분출 — 기존 기본 느낌. ① 바깥 분출 보장.
  burst: { distMul: 1.0, fall: 18, rotMul: 1.0, durationMul: 1.0, maxDelay: 0, endScale: 0.55, yEase: "Quad.easeOut" },
  // 빠른 ripple 흩뿌림 — 조각별 지연으로 차르륵 퍼지며 작아진다(회전은 약한 텀블). ① 바깥 분출 보장.
  spin: { distMul: 0.9, fall: 16, rotMul: 1.2, durationMul: 0.95, maxDelay: 40, endScale: 0.4, yEase: "Quad.easeOut" },
  // 분수 — Back.easeIn이 위로 솟는 아크를 만든 뒤 가속 낙하. ② 하강 종착 보장.
  fountain: { distMul: 0.6, fall: 36, rotMul: 0.9, durationMul: 1.25, maxDelay: 60, endScale: 0.5, yEase: "Back.easeIn" },
  // 붕괴 — 확산 최소, 조각이 거의 제자리에서 와르르 무너진다. ② 하강 종착 보장.
  collapse: { distMul: 0.35, fall: 30, rotMul: 0.7, durationMul: 1.15, maxDelay: 80, endScale: 0.7, yEase: "Quad.easeIn" },
};

/**
 * 벽돌별 균열/조각 variant 결정 — (gridX, gridY, salt) 결정론.
 * salt에 스테이지·던전 인덱스를 주면 같은 칸도 판마다 패턴이 달라진다 (미지정 0).
 * 시드 혼합 상수는 십진 소수(8191·131·524287) — hex 리터럴 회귀 게이트(themeStatic) 회피.
 */
export function pickShatterVariant(gridX: number, gridY: number, salt = 0): number {
  const r = makeRng(gridX * 8191 + gridY * 131 + salt * 524287)();
  return Math.min(SHATTER_VARIANT_COUNT - 1, Math.floor(r * SHATTER_VARIANT_COUNT));
}

export interface ShatterSpawn {
  readonly texture: string;
  /** 조각 시작 위치 — 벽돌 영역 내 조각의 원래 자리. */
  readonly x: number;
  readonly y: number;
  readonly displayWidth: number;
  readonly displayHeight: number;
  /** 비행 목표 (스타일별 확산/하강 반영). */
  readonly toX: number;
  readonly toY: number;
  /** 비행 중 회전 델타(rad). */
  readonly rotationDelta: number;
  readonly duration: number;
  /** 시작 지연(ms) — 스타일 maxDelay 안에서 조각별 ripple. */
  readonly delay: number;
}

export interface ShatterPlan {
  readonly style: ShatterStyle;
  readonly spawns: readonly ShatterSpawn[];
}

/**
 * 파괴 1회의 흩어짐 계획 — 순수 (Phaser 비의존, rng 주입 결정론).
 * stage(죽는 순간 균열 단계, 0=원샷~4=severe)로 조각을 그룹 병합한다: 같은 그룹 조각은 원위치에서
 * 출발하되 공통 비행 벡터·회전·지연으로 평행 이동 → 큰 덩어리가 통째로 날아가는 연출(stage 얕을수록
 * 적은 그룹=큰 덩어리, 깊을수록 많은 그룹=잘게). 첫 rng 호출로 스타일 추첨, 그룹마다 벡터를 추첨.
 * (cx,cy)=벽돌 중심, (dw,dh)=벽돌 displaySize. 멀티셀 스트레치는 crack 오버레이와 동일하게 비례 흡수.
 */
export function computeShatterPlan(
  cx: number,
  cy: number,
  dw: number,
  dh: number,
  variant: number,
  stage: number,
  rng: () => number = Math.random,
  /** 분출 에너지 배수 — 변위(vx,vy)에 곱(기본 1). 크리티컬 파괴 시 1보다 크게 줘 조각이 더 세게·멀리 튄다. */
  energyMul = 1,
): ShatterPlan {
  const idx = Math.min(SHATTER_VARIANT_COUNT - 1, Math.max(0, Math.floor(variant)));
  const meta = BRICK_SHATTER_VARIANTS[idx]!;
  const style = SHATTER_STYLE_NAMES[
    Math.min(SHATTER_STYLE_NAMES.length - 1, Math.floor(rng() * SHATTER_STYLE_NAMES.length))
  ]!;
  const spec = SHATTER_STYLES[style];
  const sIdx = Math.min(meta.pieceStageGroups.length - 1, Math.max(0, Math.floor(stage)));
  const groupOf = meta.pieceStageGroups[sIdx]!;

  // 조각을 그룹별로 모은다 (groupId 정렬 — rng 소비 순서 결정론).
  const byGroup = new Map<number, number[]>();
  meta.pieces.forEach((_, i) => {
    const g = groupOf[i]!;
    (byGroup.get(g) ?? byGroup.set(g, []).get(g)!).push(i);
  });

  const spawns: ShatterSpawn[] = [];
  for (const [, idxs] of [...byGroup].sort((a, b) => a[0] - b[0])) {
    // 그룹 중심(정규화 평균) → 벽돌 중심에서 바깥 방향 1개 벡터를 그룹 전체에 적용(평행 비행 = 한 덩어리).
    let mx = 0;
    let my = 0;
    for (const i of idxs) {
      mx += meta.pieces[i]!.cx;
      my += meta.pieces[i]!.cy;
    }
    const gcx = cx + (mx / idxs.length - 0.5) * dw;
    const gcy = cy + (my / idxs.length - 0.5) * dh;
    const ox = gcx - cx;
    const oy = gcy - cy;
    const len = Math.hypot(ox, oy);
    const baseAngle = len > 1e-3 ? Math.atan2(oy, ox) : rng() * Math.PI * 2;
    const angle = baseAngle + (rng() * 2 - 1) * SHATTER_ANGLE_JITTER;
    const dist = (SHATTER_FLY_MIN + SHATTER_FLY_VAR * rng()) * spec.distMul;
    // energyMul은 변위(확산·하강) 전체에 곱 — 회전(rotationDelta)에는 미적용(약한 텀블 게이트 보존).
    const vx = Math.cos(angle) * dist * energyMul;
    const vy = (Math.sin(angle) * dist + spec.fall) * energyMul;
    const rotationDelta = (rng() * 2 - 1) * BASE_ROTATION * spec.rotMul;
    const duration = BASE_DURATION * spec.durationMul * (0.85 + 0.3 * rng());
    const delay = spec.maxDelay * rng();
    for (const i of idxs) {
      const p = meta.pieces[i]!;
      const sx = cx + (p.cx - 0.5) * dw;
      const sy = cy + (p.cy - 0.5) * dh;
      spawns.push({
        texture: p.key,
        x: sx,
        y: sy,
        displayWidth: p.w * dw,
        displayHeight: p.h * dh,
        toX: sx + vx,
        toY: sy + vy,
        rotationDelta,
        duration,
        delay,
      });
    }
  }
  return { style, spawns };
}

export interface BrickShatterOptions {
  readonly x: number;
  readonly y: number;
  readonly displayWidth: number;
  readonly displayHeight: number;
  /** 벽돌 tint 승계 — gold 고정 금색/강도 무지개 그대로 (조각 PNG는 흰색 tint base). */
  readonly tint: number;
  readonly variant: number;
  /** 죽는 순간 균열 단계(0=원샷~4=severe) — 조각 그룹 병합 깊이(얕을수록 큰 덩어리). */
  readonly stage: number;
  /** 분출 에너지 배수(기본 1) — 크리티컬 파괴 시 1보다 크게 줘 조각이 더 세게·멀리 튄다. */
  readonly energyMul?: number;
  readonly rng?: () => number;
}

/** 파괴 조각 연출 emit — 셸(Image+Tween). x/y/alpha는 per-property ease로 스타일별 곡선. FX 풀에서 Image 재사용. */
export function emitBrickShatter(
  scene: Phaser.Scene,
  pool: FxImagePool,
  opts: BrickShatterOptions,
): Phaser.GameObjects.Image[] {
  const rng = opts.rng ?? Math.random;
  const plan = computeShatterPlan(
    opts.x,
    opts.y,
    opts.displayWidth,
    opts.displayHeight,
    opts.variant,
    opts.stage,
    rng,
    opts.energyMul ?? 1,
  );
  const spec = SHATTER_STYLES[plan.style];
  const out: Phaser.GameObjects.Image[] = [];
  for (const s of plan.spawns) {
    // 풀 재사용 — texture·blend·rotation·scale을 명시 재설정(직전 사용 stale 제거). rotation 0 시작(tween이 delta로 회전).
    const img = pool
      .acquire()
      .setTexture(s.texture)
      .setPosition(s.x, s.y)
      .setDepth(DEPTH.brickOverlay + 0.5)
      .setTint(opts.tint)
      .setAlpha(0.98)
      .setRotation(0)
      .setActive(true)
      .setVisible(true);
    img.setDisplaySize(s.displayWidth, s.displayHeight);
    scene.tweens.add({
      targets: img,
      x: { value: s.toX, ease: "Quad.easeOut" },
      y: { value: s.toY, ease: spec.yEase },
      // easeIn — 비행 대부분 동안 또렷하다가 막판에 빠르게 사라진다 (조각 가독 ↑).
      alpha: { value: 0, ease: "Quad.easeIn" },
      rotation: s.rotationDelta,
      scaleX: img.scaleX * spec.endScale,
      scaleY: img.scaleY * spec.endScale,
      delay: s.delay,
      duration: s.duration,
      ease: "Quad.easeOut",
      onComplete: () => pool.release(img),
    });
    out.push(img);
  }
  return out;
}
